<template>
    <div class="printer-controls">
        <!-- Connection Status -->
        <div class="status-bar" :class="{ connected: isConnected }">
            {{ statusMessage }}
            <span v-if="deviceName" class="device-name">({{ deviceName }})</span>
        </div>

        <!-- Main Action Buttons -->
        <div class="button-group">
            <button @click="handleConnect" :disabled="isConnecting || isConnected" class="action-btn connect">
                <span v-if="!isConnecting">🔌 Connect Printer</span>
                <span v-else>⏳ Connecting...</span>
            </button>

            <button @click="handlePrint" :disabled="!isConnected || isPrinting" class="action-btn print">
                <span v-if="!isPrinting">🖨️ Print Receipt</span>
                <span v-else>⏳ Printing...</span>
            </button>

            <button @click="handleDisconnect" :disabled="!isConnected" class="action-btn disconnect">
                🔌 Disconnect
            </button>
        </div>

        <!-- Test Print Button (for development) -->
        <button 
            v-if="isConnected && !isPrinting" 
            @click="handleTestPrint" 
            class="test-btn"
        >
            🧪 Test Print
        </button>

        <!-- Error Display -->
        <div v-if="errorMessage" class="error-message">
            ⚠️ {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-message">
            ✅ {{ successMessage }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import printer from '@/utils/bluetoothReceiptPrinter';

// State
const isConnecting = ref(false);
const isPrinting = ref(false);
const isConnected = ref(false);
const deviceName = ref('');
const errorMessage = ref('');
const successMessage = ref('');

// Computed status
const statusMessage = computed(() => {
    if (isConnected.value) return '✅ Printer Connected';
    if (isConnecting.value) return '⏳ Connecting to printer...';
    return '❌ Printer Not Connected';
});

// Event handlers from printer
const handlePrinterConnected = (data) => {
    console.log('Printer connected event received:', data);
    isConnected.value = true;
    deviceName.value = data.name || 'Unknown Printer';
    errorMessage.value = '';
    successMessage.value = '✅ Printer connected successfully!';
    
    // Clear success message after 3 seconds
    setTimeout(() => {
        successMessage.value = '';
    }, 3000);
};

const handlePrinterDisconnected = () => {
    console.log('Printer disconnected event received');
    isConnected.value = false;
    deviceName.value = '';
    successMessage.value = '';
};

const handlePrinterError = (error) => {
    console.error('Printer error event:', error);
    errorMessage.value = error.message || 'Printer error occurred';
    isConnected.value = false;
    deviceName.value = '';
};

// Set up event listeners on mount
onMounted(() => {
    // Add event listeners to printer
    printer.on('connected', handlePrinterConnected);
    printer.on('disconnected', handlePrinterDisconnected);
    printer.on('error', handlePrinterError);
    
    // Check initial connection status
    const status = printer.getStatus();
    isConnected.value = status.connected;
    deviceName.value = status.deviceName || '';
});

// Clean up event listeners on unmount
onUnmounted(() => {
    printer.off('connected', handlePrinterConnected);
    printer.off('disconnected', handlePrinterDisconnected);
    printer.off('error', handlePrinterError);
});

// Direct user gesture handler for connect
const handleConnect = async () => {
    // Reset messages
    errorMessage.value = '';
    successMessage.value = '';

    // Show connecting state
    isConnecting.value = true;

    try {
        // DIRECT connection - no async setup before this
        await printer.connect();
        
        // Note: isConnected will be updated by the event listener
        // We don't need to set it here
        
    } catch (error) {
        console.error('Connection error:', error);
        
        // Don't show error for user cancellation
        if (error.message && error.message.includes('User cancelled')) {
            errorMessage.value = '';
        } else {
            errorMessage.value = error.message || 'Failed to connect to printer';
        }
        
        // Ensure connected state is false
        isConnected.value = false;
    } finally {
        isConnecting.value = false;
    }
};

// Direct user gesture handler for print
const handlePrint = async () => {
    if (!isConnected.value) return;

    isPrinting.value = true;
    errorMessage.value = '';
    successMessage.value = '';

    try {
        // Prepare receipt data
        const receiptData = {
            storeName: 'YOUR STORE NAME',
            storeAddress: '123 Main Street',
            storePhone: 'Tel: 123-4567',
            receiptNumber: `R-${Date.now()}`,
            date: new Date().toLocaleString(),
            cashier: 'Cashier Name',
            items: [
                { name: 'Product 1', quantity: 2, price: 100.00 },
                { name: 'Product 2', quantity: 1, price: 250.00 }
            ],
            subtotal: 450.00,
            tax: 45.00,
            total: 495.00,
            payment: 500.00,
            change: 5.00
        };

        // Print receipt
        await printer.printReceipt(receiptData);

        // Show success message
        successMessage.value = '✅ Print job sent successfully!';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
            successMessage.value = '';
        }, 3000);

    } catch (error) {
        console.error('Print error:', error);
        errorMessage.value = error.message || 'Failed to print';
    } finally {
        isPrinting.value = false;
    }
};

// Test print handler
const handleTestPrint = async () => {
    if (!isConnected.value) return;
    
    isPrinting.value = true;
    errorMessage.value = '';
    
    try {
        const testData = {
            storeName: 'TEST PRINT',
            items: [
                { name: 'Test Item 1', quantity: 1, price: 10.00 },
                { name: 'Test Item 2', quantity: 2, price: 5.00 }
            ],
            total: 20.00,
            footer: 'TEST PRINT - IGNORE'
        };
        
        await printer.printReceipt(testData);
        
        successMessage.value = '✅ Test print successful!';
        setTimeout(() => {
            successMessage.value = '';
        }, 3000);
        
    } catch (error) {
        errorMessage.value = error.message || 'Test print failed';
    } finally {
        isPrinting.value = false;
    }
};

// Disconnect handler
const handleDisconnect = () => {
    printer.disconnect();
    // isConnected will be updated by the event listener
    errorMessage.value = '';
    successMessage.value = '❌ Printer disconnected';
    
    // Clear success message after 3 seconds
    setTimeout(() => {
        successMessage.value = '';
    }, 3000);
};
</script>

<style scoped>
.printer-controls {
    padding: 20px;
    max-width: 400px;
    margin: 0 auto;
}

.status-bar {
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 20px;
    background: #fee;
    color: #c00;
    font-weight: 500;
    text-align: center;
    transition: all 0.3s;
}

.status-bar.connected {
    background: #e8f5e9;
    color: #2e7d32;
}

.device-name {
    font-size: 12px;
    opacity: 0.8;
    margin-left: 5px;
}

.button-group {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 15px;
}

.action-btn {
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    min-width: 120px;
}

.action-btn.connect {
    background: #2196f3;
    color: white;
}

.action-btn.connect:hover:not(:disabled) {
    background: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.action-btn.print {
    background: #4caf50;
    color: white;
}

.action-btn.print:hover:not(:disabled) {
    background: #388e3c;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.action-btn.disconnect {
    background: #f44336;
    color: white;
}

.action-btn.disconnect:hover:not(:disabled) {
    background: #d32f2f;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

.action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
}

.test-btn {
    width: 100%;
    padding: 10px;
    background: #9e9e9e;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    margin-bottom: 15px;
    transition: all 0.3s;
}

.test-btn:hover {
    background: #757575;
    transform: translateY(-2px);
}

.error-message {
    margin-top: 15px;
    padding: 12px;
    background: #ffebee;
    color: #c62828;
    border-radius: 8px;
    font-size: 14px;
    border-left: 4px solid #c62828;
    animation: slideIn 0.3s;
}

.success-message {
    margin-top: 15px;
    padding: 12px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 8px;
    font-size: 14px;
    border-left: 4px solid #2e7d32;
    animation: slideIn 0.3s;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>